/**
 * Finance → Payouts.
 *
 * Reading order is the order an operator needs: where the money is, what is
 * blocking it, who is owed it, then what happened. Settlement history used to
 * sit above the seller information, which meant the page opened on the past.
 *
 * Each section owns its own loading and error state. One failing request
 * leaves the rest of the page usable — the seller table going down should not
 * take the money position with it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { api } from '../../lib/api';

import { ActionRequired, CodRemittancePanel } from './ActionRequired';
import { MoneyPosition, PaymentLifecycle } from './MoneyPosition';
import { PayoutDetailDrawer } from './PayoutDetailDrawer';
import { PayoutsHeader, type PeriodDays } from './PayoutsHeader';
import { RefundsOwedPanel } from './RefundsOwedPanel';
import { SellerLookup } from './SellerLookup';
import { SELLER_PAYOUTS_KEY, SellerPayouts, type SellerPayoutRow } from './SellerPayouts';
import { SettlementRuns } from './SettlementRuns';

export function PayoutsPage() {
  const qc = useQueryClient();
  const [period, setPeriod] = useState<PeriodDays>(30);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [selected, setSelected] = useState<SellerPayoutRow | null>(null);
  const [lookupVendorId, setLookupVendorId] = useState<string | undefined>(undefined);

  // Recomputed only when the period changes, so the query key stays stable
  // across renders and React Query does not refetch on every keystroke
  // elsewhere on the page.
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - period * 86_400_000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [period]);

  const overview = useQuery({
    queryKey: ['admin-finance-overview', range],
    queryFn: () => api.settlements.adminOverview(range),
  });

  const runs = useQuery({
    queryKey: ['admin-settlement-runs'],
    queryFn: () => api.settlements.listRuns(),
  });

  const codRemittances = useQuery({
    queryKey: ['admin-cod-remittances'],
    queryFn: () => api.settlements.listPendingCodRemittances({ page: 1, pageSize: 50 }),
  });

  // Cash marked received changes what the seller is owed, so every view of it
  // is now stale — including the cross-seller table.
  const refreshAfterCod = () => {
    void qc.invalidateQueries({ queryKey: ['admin-cod-remittances'] });
    void qc.invalidateQueries({ queryKey: ['admin-vendor-settlements'] });
    void qc.invalidateQueries({ queryKey: ['admin-vendor-eligibility'] });
    void qc.invalidateQueries({ queryKey: ['admin-finance-overview'] });
    void qc.invalidateQueries({ queryKey: [SELLER_PAYOUTS_KEY] });
  };

  const markCodRemitted = useMutation({
    mutationFn: (subOrderId: string) => api.settlements.markCodRemitted(subOrderId),
    onSuccess: refreshAfterCod,
  });

  const runSettlement = useMutation({
    mutationFn: () => api.settlements.runSettlement(),
    onSuccess: () => {
      setRunDialogOpen(false);
      // A run moves money into seller balances: the runs list, the buckets and
      // every seller row change together.
      void qc.invalidateQueries({ queryKey: ['admin-settlement-runs'] });
      void qc.invalidateQueries({ queryKey: ['admin-finance-overview'] });
      void qc.invalidateQueries({ queryKey: [SELLER_PAYOUTS_KEY] });
      void qc.invalidateQueries({ queryKey: ['admin-vendor-settlements'] });
      void qc.invalidateQueries({ queryKey: ['admin-vendor-eligibility'] });
    },
  });

  const releaseReserves = useMutation({
    mutationFn: () => api.settlements.releaseReserves(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-vendor-settlements'] });
      void qc.invalidateQueries({ queryKey: ['admin-finance-overview'] });
      void qc.invalidateQueries({ queryKey: [SELLER_PAYOUTS_KEY] });
    },
  });

  const codItems = codRemittances.data?.data ?? [];

  return (
    <div className="mx-auto max-w-[1440px] space-y-7 pb-10">
      <PayoutsHeader
        period={period}
        onPeriodChange={setPeriod}
        lastRunAt={overview.data?.lastRunAt}
        eligible={overview.data?.eligible}
        runDialogOpen={runDialogOpen}
        onOpenRunDialog={() => setRunDialogOpen(true)}
        onCloseRunDialog={() => setRunDialogOpen(false)}
        onRunSettlement={() => runSettlement.mutate()}
        runPending={runSettlement.isPending}
        onReleaseReserves={() => releaseReserves.mutate()}
        releasePending={releaseReserves.isPending}
      />

      <MoneyPosition
        overview={overview.data}
        isLoading={overview.isPending}
        isError={overview.isError}
        onRetry={() => void overview.refetch()}
      />

      <PaymentLifecycle overview={overview.data} isLoading={overview.isPending} />

      <ActionRequired
        overview={overview.data}
        codPending={codItems}
        codTotal={codRemittances.data?.total ?? 0}
        isLoading={overview.isPending || codRemittances.isPending}
        onReviewCod={() =>
          document.getElementById('cod-remittance')?.scrollIntoView({ behavior: 'smooth' })
        }
      />

      <div id="cod-remittance">
        <CodRemittancePanel
          items={codItems}
          total={codRemittances.data?.total ?? 0}
          isLoading={codRemittances.isPending}
          onMarkRemitted={(id) => markCodRemitted.mutate(id)}
          onReconciled={refreshAfterCod}
          markPending={markCodRemitted.isPending}
          markError={
            markCodRemitted.isError
              ? (markCodRemitted.error?.message ?? 'Could not mark this order as received.')
              : null
          }
        />
      </div>

      <RefundsOwedPanel />

      <SellerPayouts
        onSelect={(row) => {
          setSelected(row);
          // Opening a row also points the investigation panel at that seller,
          // so the actions for them are one scroll away rather than a cuid
          // copy-paste away.
          setLookupVendorId(row.vendorId);
        }}
      />

      <SellerLookup vendorId={lookupVendorId} />

      <SettlementRuns
        runs={runs.data}
        isLoading={runs.isPending}
        isError={runs.isError}
        onRetry={() => void runs.refetch()}
      />

      <PayoutDetailDrawer row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
